import { MultipleToggle } from '@components/MultipleToggle/MultipleToggle';
import { VideoSkeleton } from '@components/VideoSkeleton/VideoSkeleton';
import { useMediaData } from '@lib/data/mediaData';
import { useTranscript } from '@lib/data/transcript';
import { ModalTab } from '@lib/media/modalTab';
import { Divider } from '@mui/material';
import { isProcessingWithoutVideo } from '@pages/Library/utils/mediaStatus';
import { debugStore } from '@stores/Debug';
import { notificationStore } from '@stores/Notifications';
import { trackingStore } from '@stores/Tracking';
import { videoPlayerStore } from '@stores/VideoPlayer';
import { Media } from '@typings/Media';
import { observer } from 'mobx-react';
import { forwardRef, ReactNode, useEffect, useRef, useState } from 'react';
import { BaseModal, BaseModalRef } from '../BaseModal/BaseModal';
import { ProcessingPlaceholder } from '../MomentModal/components/ProcessingPlaceholder/ProcessingPlaceholder';
import { TranscriptLoader } from '../MomentModal/components/TranscriptLoader/TranscriptLoader';
import { VideoPlayer, VideoPlayerRef } from '../MomentModal/components/VideoPlayer/VideoPlayer';
import { VideoProcessingPlaceholder } from '../MomentModal/components/VideoProcessingPlaceholder/VideoProcessingPlaceholder';
import { VideoTimeline } from '../MomentModal/components/VideoTimeline/VideoTimeline';
import {
  VisualDescriptionLoader,
  VisualDescriptions,
} from '../MomentModal/components/VisualDescriptionLoader/VisualDescriptionLoader';
import { visualToggleOption } from '../momentVisualTab';
import css from './BaseMomentModal.module.scss';

export type BaseMomentModalRef = BaseModalRef;

/**
 * What this modal's toggle actually offers. It has no Scenes option, so accepting the full
 * `ModalTab` for its panel state would let `scenes` be assignable to a value whose only two
 * renderable branches render the transcript for anything that is not `visual`.
 */
type BasePanel = Extract<ModalTab, 'visual' | 'transcript'>;

type BaseMomentModalProps = {
  media: Media;
  startTime: number;
  endTime: number;
  title?: string;
  headerContent?: ReactNode;
  onOpen?: () => void;
  onClose?: () => void;
  footerContent: ReactNode;
  className?: string;
  /** When true, the library skips visual indexing (no visual descriptions): default to the
   *  transcript tab and mark the visual one unavailable. */
  isTranscriptOnly?: boolean;
};

export const BaseMomentModal = observer(
  forwardRef<BaseMomentModalRef, BaseMomentModalProps>(
    (
      {
        media,
        startTime,
        endTime,
        title,
        headerContent,
        onOpen,
        onClose,
        footerContent,
        className,
        isTranscriptOnly = false,
      },
      ref,
    ) => {
      const videoPlayerRef = useRef<VideoPlayerRef>(null);
      const [rightPanel, setRightPanel] = useState<BasePanel>('visual');
      // Transcript-only libraries have no visual descriptions → force transcript. Derived
      // (not a useState initializer) so it self-corrects if `isTranscriptOnly` arrives after
      // mount (library data loading late) instead of latching an initial 'visual'.
      const activePanel: BasePanel = isTranscriptOnly ? 'transcript' : rightPanel;
      const [isLoading, setIsLoading] = useState(true);
      const [open, setOpen] = useState(false);

      // Check if media is still processing (transcript/visual not ready yet)
      // Debug flag allows simulating processing state for any media
      const isProcessing =
        ['queued', 'uploading', 'processing'].includes(media.status) ||
        debugStore.simulateProcessingModal;

      const { data: transcript, error: transcriptError } = useTranscript(
        open && !isProcessing ? media.libraryId : undefined,
        open && !isProcessing ? media._id : undefined,
        media.status, // Cache key to trigger refetch when status changes
      );
      const { data: visualDescriptions, error: visualDescriptionsError } =
        useMediaData<VisualDescriptions>(
          'shot_descriptions.json',
          // Skip the visual fetch for transcript-only libraries — there's no visual data.
          open && !isProcessing && !isTranscriptOnly ? media.libraryId : undefined,
          open && !isProcessing && !isTranscriptOnly ? media._id : undefined,
          media.status, // Cache key to trigger refetch when status changes
        );

      const handleOpen = () => {
        setOpen(true);
        videoPlayerStore.setMoment({ start: startTime, end: endTime });
        onOpen?.();
      };

      const handleClose = () => {
        setOpen(false);
        videoPlayerStore.setMoment(null);
        videoPlayerStore.setMode('moment');
        setIsLoading(true);
        onClose?.();
      };

      // Close modal if media is in an invalid state (errored, cancelled, or empty)
      useEffect(() => {
        if (open && ['errored', 'cancelled', 'empty'].includes(media.status)) {
          if (media.status === 'errored') {
            notificationStore.pushRichEventOrMerge({
              type: 'rich-event',
              variant: 'error',
              iconKind: 'processing-error',
              header: 'Processing failed',
              description: 'There was an error processing your media.',
              rows: [
                {
                  key: media._id,
                  filename: media.metadata?.name ?? media.metadata?.filename ?? 'Untitled',
                  reason: 'Internal processing error — please try again or contact support.',
                },
              ],
              modalTitle: 'Processing errors',
              libraryId: media.libraryId,
              batchKind: 'media-processing-errored',
            });
            trackingStore.uploadRichEventShown({
              kind: 'processing-error',
              rowCount: 1,
            });
          }
          handleClose();
        }
      }, [open, media.status]);

      return (
        <BaseModal
          ref={ref}
          title={title || media.metadata?.name}
          headerContent={headerContent}
          onClose={handleClose}
          footerContent={footerContent}
          className={className}
          onOpen={handleOpen}
        >
          <div className={css.preview}>
            <div className={css.videoContainer}>
              {media.status === 'queued' || isProcessingWithoutVideo(media) ? (
                <VideoProcessingPlaceholder />
              ) : (
                <>
                  {isLoading && <VideoSkeleton className={css.skeletonVideo} />}
                  <VideoPlayer
                    media={media}
                    ref={videoPlayerRef}
                    hidden={isLoading}
                    onReady={() => setIsLoading(false)}
                    containerClassName={css.videoContainer}
                  />
                </>
              )}
            </div>
            <Divider orientation="vertical" sx={{ height: 'calc(100% + 16px)' }} />
            <div className={css.rightPanel}>
              {isProcessing || isProcessingWithoutVideo(media) ? (
                <ProcessingPlaceholder
                  hasVideo={media.status !== 'queued' && !isProcessingWithoutVideo(media)}
                />
              ) : (
                <>
                  <MultipleToggle
                    options={[
                      visualToggleOption(isTranscriptOnly),
                      { value: 'transcript', label: 'Transcript' },
                    ]}
                    value={activePanel}
                    onChange={setRightPanel}
                  />
                  {open &&
                    (activePanel === 'visual' ? (
                      <VisualDescriptionLoader
                        visualDescriptions={visualDescriptions}
                        error={visualDescriptionsError}
                      />
                    ) : (
                      <TranscriptLoader
                        transcript={transcript}
                        error={transcriptError}
                        videoPlayerRef={videoPlayerRef}
                      />
                    ))}
                </>
              )}
            </div>
          </div>
          <Divider />
          {
            <VideoTimeline
              duration={media.metadata?.video?.duration ?? 0}
              videoPlayerRef={videoPlayerRef}
              isLoading={isLoading}
              mediaId={media._id}
              libraryId={media.libraryId}
            />
          }
        </BaseModal>
      );
    },
  ),
);
